/**
 * Coerces arbitrary DOM into the frozen schema.
 *
 * The editor lets the browser perform the actual editing, so after every
 * mutation the tree can hold anything the browser felt like producing -- a
 * stray `<div>` from Safari, a `<span style>` from a paste, a list nested three
 * deep. This module rebuilds a schema-conformant tree from what is there and
 * swaps it in, but only when the result actually differs, so the common case
 * (ordinary typing, which already conforms) costs nothing and never disturbs
 * the caret.
 */

import * as bookmark from './bookmark';
import { mergeInlines } from './dom-utils';
import {
  ALLOWED_ATTRIBUTES,
  BLOCK_ALIASES,
  DISCARDED_TAGS,
  LIST_TAGS,
  LINK_REL,
  LINK_TARGET,
  MARK_TAGS,
  TAG_ALIASES,
  VOID_TAGS,
  sanitizeUrl,
} from './schema';

/** Table parts are unwrapped rather than dropped, so their text survives. */
const TABLE_CONTAINER_TAGS: ReadonlySet<string> = new Set([
  'TABLE', 'THEAD', 'TBODY', 'TFOOT', 'TR', 'CAPTION',
]);
const TABLE_CELL_TAGS: ReadonlySet<string> = new Set(['TD', 'TH']);

function createEmptyParagraph(): HTMLParagraphElement {
  const paragraph = document.createElement('p');
  paragraph.appendChild(document.createElement('br'));
  return paragraph;
}

function resolveTag(node: Element): string {
  return TAG_ALIASES.get(node.nodeName) ?? node.nodeName;
}

function isDiscarded(node: Node): boolean {
  if (node.nodeType === Node.COMMENT_NODE || node.nodeType === Node.PROCESSING_INSTRUCTION_NODE) {
    return true;
  }
  if (node.nodeType !== Node.ELEMENT_NODE) {
    return false;
  }
  // Word emits namespaced elements such as <o:p> and <w:sdt>.
  return DISCARDED_TAGS.has(node.nodeName) || node.nodeName.includes(':');
}

function isBlockSource(node: Node): boolean {
  if (node.nodeType !== Node.ELEMENT_NODE) {
    return false;
  }
  const tag = node.nodeName;
  return tag === 'P'
    || BLOCK_ALIASES.has(tag)
    || TABLE_CONTAINER_TAGS.has(tag)
    || TABLE_CELL_TAGS.has(tag);
}

/** True for a mark that carries no text and no line break, i.e. `<b></b>`. */
function isEmptyMark(node: Node): boolean {
  return node.nodeType === Node.ELEMENT_NODE
    && MARK_TAGS.has(node.nodeName)
    && (node.textContent ?? '').length === 0
    && !(node as Element).querySelector('br');
}

function haveSameAttributes(a: Element, b: Element): boolean {
  if (a.attributes.length !== b.attributes.length) {
    return false;
  }
  return Array.from(a.attributes).every((attr) => b.getAttribute(attr.name) === attr.value);
}

/**
 * Appends an inline node, merging it into the previous sibling when the two are
 * the same mark. Keeps `<b>a</b><b>b</b>` from surviving a round trip.
 */
function appendInline(parent: Element, node: Node): void {
  if (isEmptyMark(node)) {
    return;
  }

  const last = parent.lastChild;
  if (!last) {
    parent.appendChild(node);
    return;
  }

  if (last.nodeType === Node.TEXT_NODE && node.nodeType === Node.TEXT_NODE) {
    (last as Text).appendData((node as Text).data);
    return;
  }

  if (
    last.nodeType === Node.ELEMENT_NODE
    && node.nodeType === Node.ELEMENT_NODE
    && last.nodeName === node.nodeName
    && MARK_TAGS.has(last.nodeName)
    && haveSameAttributes(last as Element, node as Element)
  ) {
    for (const child of Array.from(node.childNodes)) {
      appendInline(last as Element, child);
    }
    return;
  }

  parent.appendChild(node);
}

function buildLink(source: Element): Element | null {
  const href = sanitizeUrl(source.getAttribute('href') ?? '');
  if (!href) {
    return null;
  }
  const anchor = document.createElement('a');
  anchor.setAttribute('href', href);
  anchor.setAttribute('target', LINK_TARGET);
  anchor.setAttribute('rel', LINK_REL);
  return anchor;
}

/**
 * Word and Google Docs express formatting as inline styles on `<span>` rather
 * than as tags. Recovers the two marks we keep and drops the rest.
 */
function marksFromStyle(source: Element): readonly string[] {
  const style = source.getAttribute('style');
  if (!style) {
    return [];
  }
  const lower = style.toLowerCase();
  const marks: string[] = [];

  const weight = /font-weight\s*:\s*([^;]+)/.exec(lower)?.[1]?.trim();
  if (weight && (weight === 'bold' || weight === 'bolder' || Number(weight) >= 600)) {
    marks.push('B');
  }
  if (/font-style\s*:\s*italic/.test(lower)) {
    marks.push('I');
  }
  return marks;
}

/** Appends `source`'s content to `target` as schema-conformant inline nodes. */
function buildInline(source: Node, target: Element): void {
  if (isDiscarded(source)) {
    return;
  }

  if (source.nodeType === Node.TEXT_NODE) {
    appendInline(target, document.createTextNode((source as Text).data));
    return;
  }

  if (source.nodeType !== Node.ELEMENT_NODE) {
    return;
  }

  const element = source as Element;
  const tag = resolveTag(element);

  if (VOID_TAGS.has(tag)) {
    appendInline(target, document.createElement('br'));
    return;
  }

  if (tag === 'A') {
    const anchor = buildLink(element);
    if (!anchor) {
      // An unusable href (or a `javascript:` one) loses the link, keeps the text.
      for (const child of Array.from(element.childNodes)) {
        buildInline(child, target);
      }
      return;
    }
    for (const child of Array.from(element.childNodes)) {
      buildInline(child, anchor);
    }
    appendInline(target, anchor);
    return;
  }

  if (MARK_TAGS.has(tag)) {
    // Google Docs wraps whole documents in <b style="font-weight:normal">.
    const styleMarks = marksFromStyle(element);
    const styleSaysNotBold = element.hasAttribute('style')
      && tag === 'B'
      && !styleMarks.includes('B');

    if (styleSaysNotBold) {
      for (const child of Array.from(element.childNodes)) {
        buildInline(child, target);
      }
      return;
    }

    const mark = document.createElement(tag.toLowerCase());
    for (const child of Array.from(element.childNodes)) {
      buildInline(child, mark);
    }
    appendInline(target, mark);
    return;
  }

  // Unknown inline element: recover any styling it expressed, then unwrap it.
  const wrappers = marksFromStyle(element);
  let host = target;
  const created: Element[] = [];
  for (const markTag of wrappers) {
    const mark = document.createElement(markTag.toLowerCase());
    created.push(mark);
    host = mark;
  }
  for (const child of Array.from(element.childNodes)) {
    buildInline(child, host);
  }
  for (let i = created.length - 1; i >= 0; i--) {
    const mark = created[i]!;
    const parent = i === 0 ? target : created[i - 1]!;
    appendInline(parent, mark);
  }
}

/** Ensures a block is never visually collapsed by giving it a `<br>` to hold. */
function finalizeBlock(block: Element): Element {
  if (block.childNodes.length === 0) {
    block.appendChild(document.createElement('br'));
  }
  return block;
}

function buildList(source: Element): Element {
  const list = document.createElement(source.nodeName === 'OL' ? 'ol' : 'ul');

  const addItem = (children: readonly Node[]): void => {
    const item = document.createElement('li');
    const spilled: Element[] = [];

    for (const child of children) {
      if (child.nodeType === Node.ELEMENT_NODE && LIST_TAGS.has(child.nodeName)) {
        // The schema is one level deep, so a nested list becomes further items.
        const nested = buildList(child as Element);
        spilled.push(...Array.from(nested.children));
        continue;
      }
      buildInline(child, item);
    }

    list.appendChild(finalizeBlock(item));
    for (const extra of spilled) {
      list.appendChild(extra);
    }
  };

  for (const child of Array.from(source.childNodes)) {
    if (isDiscarded(child)) {
      continue;
    }
    if (child.nodeName === 'LI') {
      addItem(Array.from(child.childNodes));
      continue;
    }
    if (child.nodeType === Node.ELEMENT_NODE && LIST_TAGS.has(child.nodeName)) {
      const nested = buildList(child as Element);
      for (const item of Array.from(nested.children)) {
        list.appendChild(item);
      }
      continue;
    }
    if (child.nodeType === Node.TEXT_NODE && (child as Text).data.trim().length === 0) {
      continue;
    }
    addItem([child]);
  }

  return list;
}

/** Rebuilds `source`'s children as a flat list of root-level blocks. */
function buildBlocks(source: Node): Element[] {
  const blocks: Element[] = [];
  let paragraph: Element | null = null;

  const openParagraph = (): Element => {
    if (!paragraph) {
      paragraph = document.createElement('p');
      blocks.push(paragraph);
    }
    return paragraph;
  };
  const closeParagraph = (): void => {
    paragraph = null;
  };

  for (const child of Array.from(source.childNodes)) {
    if (isDiscarded(child)) {
      continue;
    }

    if (child.nodeType === Node.ELEMENT_NODE && LIST_TAGS.has(child.nodeName)) {
      closeParagraph();
      const list = buildList(child as Element);
      if (list.children.length > 0) {
        blocks.push(list);
      }
      continue;
    }

    if (child.nodeName === 'LI') {
      closeParagraph();
      const list = document.createElement('ul');
      const item = document.createElement('li');
      for (const grandChild of Array.from(child.childNodes)) {
        buildInline(grandChild, item);
      }
      list.appendChild(finalizeBlock(item));
      blocks.push(list);
      continue;
    }

    if (isBlockSource(child)) {
      closeParagraph();
      blocks.push(...buildBlocks(child));
      closeParagraph();
      continue;
    }

    if (child.nodeType === Node.TEXT_NODE && (child as Text).data.length === 0) {
      continue;
    }

    buildInline(child, openParagraph());
  }

  return blocks
    .filter((block) => LIST_TAGS.has(block.nodeName) || block.childNodes.length > 0 || blocks.length === 1)
    .map((block) => (LIST_TAGS.has(block.nodeName) ? block : finalizeBlock(block)))
    .map(canonicalize);
}

/**
 * Applies the same inline clean-up the commands use, so that content arriving
 * through a paste or a stored value serializes identically to content produced
 * by the toolbar. Without this, `<b><a>` and `<a><b>` are both valid and the
 * serialized string is unstable -- which would fire spurious autosave writes.
 */
function canonicalize(block: Element): Element {
  if (LIST_TAGS.has(block.nodeName)) {
    for (const item of Array.from(block.children)) {
      mergeInlines(item);
    }
    return block;
  }
  mergeInlines(block);
  return block;
}

/**
 * Rewrites `root` so it conforms to the schema. Returns whether anything
 * changed; when it did, the selection is carried across the swap.
 */
export function normalize(root: HTMLElement): boolean {
  const scratch = document.createElement('div');
  const blocks = buildBlocks(root.cloneNode(true));
  if (blocks.length === 0) {
    blocks.push(createEmptyParagraph());
  }
  scratch.append(...blocks);

  if (scratch.innerHTML === root.innerHTML) {
    return false;
  }

  const mark = bookmark.save(root);
  root.replaceChildren(...Array.from(scratch.childNodes));
  bookmark.restore(root, mark);
  return true;
}

/** Builds a schema-conformant fragment from a detached source tree. */
export function normalizeFragment(source: Node): DocumentFragment {
  const fragment = document.createDocumentFragment();
  for (const block of buildBlocks(source)) {
    fragment.appendChild(block);
  }
  return fragment;
}
